// Fenced-code highlighting: highlight.js core with a curated set of
// languages, a conservative automatic fallback, and a small cache so the
// editor's live preview only re-highlights the blocks that changed.

import hljs from 'highlight.js/lib/core'
import bash from 'highlight.js/lib/languages/bash'
import css from 'highlight.js/lib/languages/css'
import diff from 'highlight.js/lib/languages/diff'
import ini from 'highlight.js/lib/languages/ini'
import javascript from 'highlight.js/lib/languages/javascript'
import json from 'highlight.js/lib/languages/json'
import markdown from 'highlight.js/lib/languages/markdown'
import plaintext from 'highlight.js/lib/languages/plaintext'
import python from 'highlight.js/lib/languages/python'
import shell from 'highlight.js/lib/languages/shell'
import sql from 'highlight.js/lib/languages/sql'
import swift from 'highlight.js/lib/languages/swift'
import typescript from 'highlight.js/lib/languages/typescript'
import xml from 'highlight.js/lib/languages/xml'
import yaml from 'highlight.js/lib/languages/yaml'

hljs.registerLanguage('bash', bash) // sh, zsh
hljs.registerLanguage('console', shell) // a shell session with `$` prompts
hljs.registerLanguage('css', css)
hljs.registerLanguage('diff', diff) // patch
hljs.registerLanguage('ini', ini) // toml
hljs.registerLanguage('javascript', javascript) // js, jsx, mjs, cjs
hljs.registerLanguage('json', json) // jsonc, json5
hljs.registerLanguage('markdown', markdown) // md
hljs.registerLanguage('plaintext', plaintext) // text, txt
hljs.registerLanguage('python', python) // py
hljs.registerLanguage('sql', sql)
hljs.registerLanguage('swift', swift)
hljs.registerLanguage('typescript', typescript) // ts, tsx
hljs.registerLanguage('xml', xml) // html, svg, plist
hljs.registerLanguage('yaml', yaml) // yml
// `shell` in a fence almost always means a script, as GitHub reads it.
hljs.registerAliases(['shell', 'shellscript'], { languageName: 'bash' })
hljs.registerAliases(['plain', 'none'], { languageName: 'plaintext' })

// Candidates for unlabelled blocks. Detection is only trusted when it is
// clear-cut: a plan's directory trees and prose snippets stay plain rather
// than coloured as a random language.
const AUTO_SUBSET = ['typescript', 'javascript', 'python', 'bash', 'json', 'yaml', 'ini', 'sql', 'swift', 'xml', 'css']
const AUTO_MAX_LENGTH = 3000
const AUTO_MIN_RELEVANCE = 5
const AUTO_CLEAR_MARGIN = 2
const AUTO_SURE_RELEVANCE = 10

export interface HighlightedCode {
  /** Escaped HTML, highlight.js spans included. */
  html: string
  /** The language used, or null for plain text. */
  language: string | null
}

const CACHE_LIMIT = 300
const CACHE_MAX_CODE = 64 * 1024
const cache = new Map<string, HighlightedCode>()

function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'))
}

/** The registered language a fence label names, or null. */
export function resolveLanguage(label: string): string | null {
  const name = label.trim().toLowerCase()
  if (!name) return null
  return hljs.getLanguage(name) ? name : null
}

function sniff(code: string): string | null {
  const head = code.trimStart()
  if (/^(?:diff --git |--- \S|@@ -\d)/.test(head)) return 'diff'
  if (/^\$ \S/.test(head)) return 'console'
  if (/^[[{]/.test(head)) {
    try {
      JSON.parse(code)
      return 'json'
    } catch {
      // not JSON; let detection decide
    }
  }
  return null
}

function detect(code: string): HighlightedCode {
  const sniffed = sniff(code)
  if (sniffed) return { html: hljs.highlight(code, { language: sniffed, ignoreIllegals: true }).value, language: sniffed }
  if (code.length <= AUTO_MAX_LENGTH) {
    const best = hljs.highlightAuto(code, AUTO_SUBSET)
    const runnerUp = best.secondBest?.relevance ?? 0
    const clear = best.relevance >= AUTO_SURE_RELEVANCE || best.relevance - runnerUp >= AUTO_CLEAR_MARGIN
    if (best.language && best.relevance >= AUTO_MIN_RELEVANCE && clear) {
      return { html: best.value, language: best.language }
    }
  }
  return { html: escapeHtml(code), language: null }
}

const PLAIN_TEXT = hljs.getLanguage('plaintext')

function compute(code: string, label: string): HighlightedCode {
  const language = resolveLanguage(label)
  if (language && hljs.getLanguage(language) === PLAIN_TEXT) {
    return { html: escapeHtml(code), language: null }
  }
  if (language) {
    return { html: hljs.highlight(code, { language, ignoreIllegals: true }).value, language }
  }
  return detect(code)
}

/** Highlights one code block; `label` is the fence's language word, or ''. */
export function highlightCode(code: string, label: string): HighlightedCode {
  if (code.length > CACHE_MAX_CODE) return compute(code, label)
  const key = `${label}\u0000${code}`
  const hit = cache.get(key)
  if (hit) {
    // Refresh recency: Map iteration order is insertion order.
    cache.delete(key)
    cache.set(key, hit)
    return hit
  }
  const result = compute(code, label)
  cache.set(key, result)
  if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value!)
  return result
}

/** Test hook: drop cached highlighting so timings measure real work. */
export function clearHighlightCache(): void {
  cache.clear()
}
